<script setup lang="ts">
import moment from 'moment'
import { computed, h, onMounted, onUnmounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'

import { useAppStore } from '@/store/app'
import { useCompanyStore } from '@/store/company'
import {
    AlertTriangleIcon,
    ArrowLeftIcon,
    ChevronDownIcon,
    File02Icon,
    FilterLinesIcon,
    PauseCircleIcon,
    PlayIcon,
    Power01Icon,
    SearchSmIcon,
    Settings01Icon,
} from '@gohighlevel/ghl-icons/24/outline'
import { UITextSmMedium } from '@gohighlevel/ghl-ui'
import {
    HLAlert,
    HLAvatarGroup,
    HLBadge,
    HLButton,
    HLCheckbox,
    HLDataTable,
    HLDataTableWrapper,
    HLDropdown,
    HLEmpty,
    HLIcon,
    HLInput,
    HLModal,
    HLPagination,
    HLSkeleton,
    HLText,
    HLTooltip,
    useHLNotification
} from '@platform-ui/highrise'
import { ReportingService } from '../../services/ReportingService'
import ScheduleSettingsModal from './ScheduleSettingsModal.vue'
import ScheduleStatusTag from './ScheduleStatusTag.vue'
import TagGroup from './TagGroup.vue'

const { t } = useI18n()
const router = useRouter()
const appStore = useAppStore()
const companyStore = useCompanyStore()

const isLoading = ref(true)
const isSettingsModalVisible = ref(false)
const isBulkActionInProgress = ref(false)

const company = ref({
    id: '',
    name: '',
    rollupAutoScheduleEnabled: true,
})

const table = ref({
    isLoading: false,
    data: [],
    computedData: [],
    search: { text: '', timer: null },
    sort: {
        column: null,
        direction: null,
    },
    totalCount: 0,
    filter: {
        status: 'all'
    }
})

const paginationConfig = ref({
    page: 1,
    limit: 10,
    options: [
        {
            key: 10,
            label: '10',
        },
        {
            key: 50,
            label: '50',
        },
        {
            key: 100,
            label: '100',
        },
        {
            key: 200,
            label: '200',
        },
        {
            key: 300,
            label: '300',
        },]
})

const notification = useHLNotification()

const tableInstance = ref(null)

const selectedRows = ref<string[]>([])
const excludedReports = ref<string[]>([])
const isAllReportsSelected = ref(false)

const columns = computed(() => [
    {
        id: 'checkbox',
        header: () => {
            return h(HLCheckbox, {
                checked: table.value.data.every(row => selectedRows.value.includes(row.id)),
                indeterminate: selectedRows.value?.length > 0 && table.value.data.some(row => selectedRows.value.includes(row.id)) && !table.value.data.every(row => selectedRows.value.includes(row.id)),
                id: 'pg_agency_location_reporting_schedule_list_table-checkbox-schedule_header',
                onUpdateChecked: val => toggleAllRowsSelection(val),
                style: {
                    justifyContent: 'center',
                },
                ariaLabel: t('selectAllSchedules'),
            })
        },
        size: 3,
        minSize: 3,
        cellFormatter: ({ row }) => {
            return h(HLCheckbox, {
                checked: selectedRows.value.includes(row.original.id),
                id: `pg_agency_location_reporting_schedule_list_table-checkbox-schedule_row_${row.original.id}`,
                style: {
                    justifyContent: 'center',
                },
                onUpdateChecked: () => toggleRowSelection(row.original.id),
                ariaLabel: t('selectScheduleForLocation', { location: row.original.subAccName }),
            })
        },
        meta: {
            align: 'center',
        },
    },
    {
        id: 'subAccName',
        header: t('subAccountName'),
        accessorKey: 'subAccName',
        meta: {
            headerAlign: 'left',
        },
        size: 16,
        minSize: 16,
    },
    {
        id: 'emailSubject',
        header: t('emailSubject'),
        accessorKey: 'emailSubject',
        meta: {
            headerAlign: 'left',
        },
        size: 22,
        minSize: 22,
    },
    {
        id: 'reportSections',
        header: t('reportSectionsAdded'),
        accessorKey: 'reportSections',
        meta: {
            headerAlign: 'left',
        },
        cellFormatter: ({ row }) => h(TagGroup, {
            tags: row.original.reportSections,
            tagProps: {
                round: true,
                size: 'md',
                interactive: false,
                bordered: false,
                class: 'w-fit'
            },
        }),
        size: 24,
        minSize: 24,
    },
    {
        id: 'frequency',
        header: t('frequency'),
        accessorKey: 'frequency',
        meta: {
            headerAlign: 'left',
        },
        size: 8,
        minSize: 8,
    },
    {
        id: 'nextRunDate',
        header: t('nextScheduleDate'),
        accessorKey: 'nextRunDate',
        meta: {
            headerAlign: 'left',
        },
        size: 10,
        minSize: 10,
    },
    {
        id: 'recipients',
        header: t('recipients'),
        accessorKey: 'recipients',
        meta: {
            headerAlign: 'left',
        },
        size: 5,
        minSize: 5,
        cellFormatter: ({ row }) => {
            return h(HLAvatarGroup, {
                id: `pg_agency_location_reporting_schedule_list_table-avatar-recipients_${row.original.id}`,
                options: row.original.recipients.map(recipient => ({
                    name: recipient,
                })),
                size: 'xs',
                stacked: true,
                tooltip: true,
                max: 4,
            })
        },
    },
    {
        id: 'status',
        header: t('status'),
        accessorKey: 'status',
        meta: {
            headerAlign: 'left',
            align: 'left',
        },
        size: 7,
        minSize: 7,
        cellFormatter: ({ row }) => {
            let status
            if (row.original.locationStatus === 'deleted') {
                status = 'location_deleted'
            } else if (row.original.locationStatus === 'inactive') {
                status = 'location_paused'
            } else if (['paused', 'draft'].includes(row.original.status)) {
                status = 'schedule_paused'
            } else {
                status = 'schedule_active'
            }

            return h(ScheduleStatusTag, {
                status,
            })
        },
    },
    {
        id: 'actions',
        header: t('actions'),
        size: 5,
        minSize: 5,
        cellFormatter: ({ row }) => {
            const showActionButton = row.original.locationStatus === 'active'
            const isPaused = ['paused', 'draft'].includes(row.original.status)

            return showActionButton ? h(HLTooltip, { variant: 'dark' }, {
                trigger: h(HLButton, {
                    id: isPaused ? `pg_agency_location_reporting_schedule_list_table-btn-action_resume_${row.original.id}` : `pg_agency_location_reporting_schedule_list_table-btn-action_pause_${row.original.id}`,
                    size: 'md',
                    variant: "ghost",
                    onClick: () => handleActionClick(row),
                    ariaLabel: isPaused ? t('resumeScheduleForLocation', { location: row.original.subAccName }) : t('pauseScheduleForLocation', { location: row.original.subAccName })
                }, {
                    icon: h(isPaused ? PlayIcon : PauseCircleIcon, {
                        class: 'h-5 w-5',
                        ariaHidden: true
                    }),
                }),
                default: isPaused ? t('resume') : t('pause')
            }) : null
        },
    },
])

const selectedScheduleCount = computed(() => isAllReportsSelected.value ? table.value.isLoading ? table.value.totalCount : table.value.totalCount - table.value.data.length + selectedRows.value.length : selectedRows.value.length)

const statusFilterOptions = computed(() => [
    {
        key: 'all',
        label: t('all'),
        type: 'icon',
        icon: File02Icon,
        iconPlacement: 'left',
        selected: table.value.filter.status === 'all',
    },
    {
        key: 'published',
        label: t('active'),
        type: 'icon',
        icon: Power01Icon,
        iconPlacement: 'left',
        selected: table.value.filter.status === 'published',
    },
    {
        key: 'draft',
        label: t('paused'),
        type: 'icon',
        icon: PauseCircleIcon,
        iconPlacement: 'left',
        selected: table.value.filter.status === 'draft',
    },
])

const actionModalData = ref({
    isVisible: false,
    data: null,
    isPrimaryBtnLoading: false,
    get content() {
        if (!this.data) return ''

        if (this.data?.locationStatus === 'deleted') {
            return t('subAccountIsDeleted')
        } else if (this.data?.locationStatus === 'inactive') {
            return t('subAccountIsPaused')
        }

        return this.data?.status === 'published'
            ? t('pauseScheduleConfirm')
            : t('resumeScheduleConfirm')
    }
})

const bulkActionModalData = ref({
    isVisible: false,
    isPrimaryBtnLoading: false,
    mode: undefined,
    get content() {
        const count = isAllReportsSelected.value ? table.value.totalCount : selectedRows.value.length
        return this.mode === 'pause' ? t('scheduleBulkPauseConfirm', { count }) : t('scheduleBulkResumeConfirm', { count })
    }
})

const bulkStatusPollingInterval = ref<number | null>(null)

watch(() => table.value.search.text, (newVal) => {
    if (table.value.search.timer) {
        clearTimeout(table.value.search.timer)
    }

    table.value.search.timer = setTimeout(async () => {
        paginationConfig.value.page = 1
        await fetchData()
        if (table.value.data.length === 0 && newVal.length === 0) {
            isLoading.value = false
        }
    }, 500)
}, { deep: true })

const backToAccount = () => {
    const path = '/sub-accounts'
    if (appStore.appHandshake) {
        appStore.appHandshake.then(parent => {
            parent.emit('route-change', { path })
        })
    }
    router.push(path)
}

const loadCompany = () => {
    if (!isAdmin()) {
        backToAccount()
        return
    }

    if (!Object.keys(companyStore.companyData).length) {
        setTimeout(loadCompany, 1000)
        return
    }

    company.value.id = companyStore.companyData.id
    company.value.name = companyStore.companyData.name
    company.value.rollupAutoScheduleEnabled = companyStore.companyData.rollup_auto_schedule_enabled ?? true
}

const fetchData = async () => {
    try {
        table.value.isLoading = true

        const paramsObject = {
            page: paginationConfig.value.page,
            limit: paginationConfig.value.limit,
            search: table.value.search.text || undefined,
            status: table.value.filter.status !== 'all' ? table.value.filter.status : undefined,
            companyId: companyStore.companyData.id,
        }

        const { data } = await ReportingService.getSchedulesList(paramsObject)
        table.value.totalCount = data.totalCount
        await populateTable(data.data)
    } catch (e) {
        console.error(e)
        const n = notification.create({
            content: () => h(HLAlert,
                {
                    title: t('error'),
                    closable: true,
                    id: 'pg_agency_location_reporting-notification-error_schedule_list',
                    onClose: () => {
                        n.destroy()
                    },
                    type: 'notification',
                    color: 'red'
                },
                { default: t('somethingWrong') }
            ),
            duration: 5000,
        })
    } finally {
        table.value.isLoading = false
    }
}

const populateTable = async (data) => {
    table.value.data = []
    if (data) {
        for (const item of data) {
            table.value.data.push({
                id: item._id,
                subAccName: item.locationName,
                locationId: item.locationId,
                emailSubject: item.subject,
                frequency: t(item.frequency),
                nextRunDate: moment(item.nextRunTime)
                    .tz(item.locationTimezone)
                    .format('lll'),
                recipients: item.recipients,
                reportSections: transformInsights(item.insights),
                status: item.status,
                locationStatus: item.locationStatus,
            })
        }
        table.value.computedData = table.value.data

        if (isAllReportsSelected.value) {
            selectedRows.value = table.value.data.map(row => row.id)
        }
    }
}

const pauseResumeSchedule = async () => {
    const newStatus = actionModalData.value.data.status === 'published' ? 'draft' : 'published'
    try {
        actionModalData.value.isPrimaryBtnLoading = true
        const id = actionModalData.value.data.id
        await ReportingService.updateScheduleStatus(
            id,
            {
                status: newStatus,
            },
            { locationId: actionModalData.value.data.locationId }
        )

        closeActionModal()
        const n = notification.create({
            content: () => h(HLAlert,
                {
                    title: t('success'),
                    closable: true,
                    id: 'pg_agency_location_reporting-notification-success_schedule_update',
                    onClose: () => {
                        n.destroy()
                    },
                    type: 'notification',
                    color: 'green'
                },
                { default: t('scheduleUpdatedSuccessfully') }
            ),
            duration: 5000,
        })
        setRowStatusById(id, newStatus)
    } catch (e) {
        console.error(e)
        const n = notification.create({
            content: () => h(HLAlert,
                {
                    title: t('error'),
                    closable: true,
                    id: 'pg_agency_location_reporting-notification-error_schedule_update',
                    onClose: () => {
                        n.destroy()
                    },
                    type: 'notification',
                    color: 'red'
                },
                { default: t('somethingWrong') }
            ),
            duration: 5000,
        })
    } finally {
        actionModalData.value.isPrimaryBtnLoading = false
    }
}

const setRowStatusById = (id: string, newStatus: string) => {
    table.value.data = table.value.data.map(row => row.id === id ? { ...row, status: newStatus } : row)
    table.value.computedData = table.value.data
}

const pageChange = async (val: number) => {
    paginationConfig.value.page = val
    await fetchData()
}

const rowCountChange = async (val: number) => {
    paginationConfig.value.limit = val
    tableInstance.value?.table?.setPageSize(val)
    await fetchData()
}

const transformInsights = (insights: string[]) => {
    return insights
        .map(insight => {
            switch (insight) {
                case 'tasks':
                    return t('tasks')
                case 'conversations':
                    return t('conversations')
                case 'users':
                    return t('users')
                case 'contacts':
                    return t('contacts')
                case 'outreach':
                    return t('outreach')
                case 'submissions':
                    return t('submissions')
                case 'sms':
                    return 'SMS'
                case 'appointments':
                    return t('appointments')
                case 'opportunities':
                    return t('dealOverview')
                case 'email_engagement':
                    return t('emailEngagement')
                case 'email_reactions':
                    return t('emailReaction')
                case 'email_delivery':
                    return t('emailDeliverySummary')
                default:
                    return (
                        insight.charAt(0).toUpperCase() + insight.slice(1)
                    ).replace('_', ' ')
            }
        })
}

const isAdmin = () => {
    return appStore.user?.role === 'admin' && appStore.user?.type === 'agency'
}

const fetchInitialData = async () => {
    try {
        if (!appStore.companyId) {
            setTimeout(fetchInitialData, 1000)
            return
        }

        loadCompany()
        await fetchData()
        isLoading.value = false
    } catch (error) {
        console.error(error)
        isLoading.value = false
    }
}

const handleStatusFilterChange = (key) => {
    table.value.filter.status = key
    paginationConfig.value.page = 1
    fetchData()
}

const toggleAllRowsSelection = (val) => {
    if (val) {
        if (isAllReportsSelected.value) {
            const currentPageIds = table.value.data.map(row => row.id)
            excludedReports.value = excludedReports.value.filter(id => !currentPageIds.includes(id))
        }
        const currentPageIds = table.value.data.map(row => row.id)
        selectedRows.value = [...new Set([...selectedRows.value, ...currentPageIds])]
    } else {
        if (isAllReportsSelected.value) {
            const currentPageIds = table.value.data.map(row => row.id)
            excludedReports.value.push(...currentPageIds.filter(id => !excludedReports.value.includes(id)))
        }
        const idsToRemove = table.value.data.map(row => row.id)
        selectedRows.value = selectedRows.value.filter(id => !idsToRemove.includes(id))
    }
}

const toggleRowSelection = (id: string) => {
    if (isAllReportsSelected.value) {
        if (excludedReports.value.includes(id)) {
            selectedRows.value.push(id)
            excludedReports.value = excludedReports.value.filter(excludedId => excludedId !== id)
        } else {
            selectedRows.value = selectedRows.value.filter(selectedId => selectedId !== id)
            excludedReports.value.push(id)
        }
    } else {
        if (selectedRows.value.includes(id)) {
            selectedRows.value = selectedRows.value.filter(selectedId => selectedId !== id)
        } else {
            selectedRows.value.push(id)
        }
    }
}

const selectAllReports = () => {
    isAllReportsSelected.value = true
    selectedRows.value = table.value.data.map(row => row.id)
    excludedReports.value = []
}

const deselectAllReports = () => {
    isAllReportsSelected.value = false
    selectedRows.value = []
    excludedReports.value = []
}

onMounted(() => {
    fetchInitialData()
    // Added timeout as company data is not available immediately for checking bulk action status
    setTimeout(async () => {
        const isBulkActionInProgress = await checkBulkUpdateStatus()
        if (isBulkActionInProgress) {
            startBulkStatusPolling()
        }
    }, 500)
})

onUnmounted(() => {
    stopBulkStatusPolling()
})

const handleActionClick = (row) => {
    actionModalData.value.isVisible = true
    actionModalData.value.data = row.original
}

const closeActionModal = () => {
    actionModalData.value.isVisible = false
    actionModalData.value.data = null
}

const handleActionModalConfirm = () => {
    pauseResumeSchedule()
}

const handleBulkActionClick = (mode?: string) => {
    bulkActionModalData.value.isVisible = true
    bulkActionModalData.value.mode = mode
}

const closeBulkActionModal = () => {
    bulkActionModalData.value.isVisible = false
}

const checkBulkUpdateStatus = async (): Promise<boolean> => {
    try {
        if (company.value.id) {
            const { data: bulkUpdateStatus } = await ReportingService.getBulkUpdateScheduleStatus(company.value.id)
            const previousStatus = isBulkActionInProgress.value
            isBulkActionInProgress.value = bulkUpdateStatus.data.inProgress

            if (!bulkUpdateStatus.data.inProgress) {
                stopBulkStatusPolling()
                // Show notification if bulk action was in progress before and completed based on API response
                if (previousStatus) {
                    const n = notification.create({
                        content: () => h(HLAlert,
                            {
                                title: t('success'),
                                closable: true,
                                id: 'pg_agency_location_reporting-notification-completed_bulk_action',
                                onClose: () => {
                                    n.destroy()
                                },
                                type: 'notification',
                                color: 'green'
                            },
                            { default: t('scheduleBulkActionCompleted') }
                        ),
                        duration: 5000,
                    })
                    await fetchData()
                }
                return false
            } else {
                return true
            }
        }
        return true
    } catch (e) {
        console.error('Error checking bulk update status:', e)
        stopBulkStatusPolling()
        return false
    }
}

const startBulkStatusPolling = () => {
    if (!bulkStatusPollingInterval.value) {
        bulkStatusPollingInterval.value = window.setInterval(checkBulkUpdateStatus, 5000)
    }
}

const stopBulkStatusPolling = () => {
    if (bulkStatusPollingInterval.value) {
        clearInterval(bulkStatusPollingInterval.value)
        bulkStatusPollingInterval.value = null
        isBulkActionInProgress.value = false
    }
}

const handleBulkActionModalConfirm = async () => {
    try {
        bulkActionModalData.value.isPrimaryBtnLoading = true
        const isResume = bulkActionModalData.value.mode === 'resume'
        const body = {
            newStatus: isResume ? 'published' : 'draft',
        }
        if (isAllReportsSelected.value) {
            body.updateAll = true
            body.search = table.value.search.text
            body.currentStatus = table.value.filter.status !== 'all' ? table.value.filter.status : undefined
            body.excludeIds = excludedReports.value
        } else {
            body.scheduleIds = selectedRows.value
            body.updateAll = false
        }

        await ReportingService.bulkUpdateScheduleStatus(body, { companyId: company.value.id })

        const n = notification.create({
            content: () => h(HLAlert,
                {
                    title: t('success'),
                    closable: true,
                    id: 'pg_agency_location_reporting-notification-success_bulk_action',
                    onClose: () => {
                        n.destroy()
                    },
                    type: 'notification',
                    color: 'green'
                },
                { default: isResume ? t('scheduleBulkActionResumeSuccess') : t('scheduleBulkActionPauseSuccess') }
            ),
            duration: 5000,
        })
        closeBulkActionModal()
        isBulkActionInProgress.value = true
        deselectAllReports()
        startBulkStatusPolling()
    } catch (e) {
        console.error(e)
        if (e.isAxiosError && e.response.status === 409) {
            isBulkActionInProgress.value = true
            const n = notification.create({
                content: () => h(HLAlert,
                    {
                        title: t('error'),
                        closable: true,
                        id: 'pg_agency_location_reporting-notification-error_bulk_action_conflict',
                        onClose: () => {
                            n.destroy()
                        },
                        type: 'notification',
                        color: 'red'
                    },
                    { default: t('bulkActionCreationError') }
                ),
                duration: 5000,
            })
        } else {
            const n = notification.create({
                content: () => h(HLAlert,
                    {
                        title: t('error'),
                        closable: true,
                        id: 'pg_agency_location_reporting-notification-error_bulk_action',
                        onClose: () => {
                            n.destroy()
                        },
                        type: 'notification',
                        color: 'red'
                    },
                    { default: t('somethingWrong') }
                ),
                duration: 5000,
            })
        }
    } finally {
        bulkActionModalData.value.isPrimaryBtnLoading = false
    }
}

const onBulkPauseSchedule = () => {
    isBulkActionInProgress.value = true
    startBulkStatusPolling()
}

const handleAutoScheduleUpdate = (value: boolean) => {
    company.value.rollupAutoScheduleEnabled = value
}
</script>

<template>
    <div id="pg-agency-location-reporting" class="container-height h-[calc(100vh-140px)]" role="main">
        <div class="mt-3">
            <div class="flex justify-between items-end mb-4">
                <div>
                    <HLButton id="pg-agency-location-reporting__btn-back-to-account" variant="text" class="font-bold"
                        color="blue" size="md" @click.prevent="backToAccount">
                        <UITextSmMedium
                            class="inline-flex cursor-pointer content-center items-center font-bold text-blue-500">
                            <ArrowLeftIcon class="mr-3 h-5 w-5" />
                            {{ t('back') }}
                        </UITextSmMedium>
                    </HLButton>
                    <div v-if="!isLoading" class="mt-1">
                        <HLText id="pg_agency_location_reporting-heading-schedule_list" size="sm" weight="medium"
                            type="display" html-tag="h1">
                            {{ t('scheduledReports') }}
                        </HLText>
                    </div>
                    <div v-else class="mt-1">
                        <HLSkeleton width="250px" height="38px" id="pg_agency_location_reporting-skeleton-schedule_list_heading" :round="false" />
                    </div>
                </div>

                <HLButton id="pg-agency-location-reporting__btn-settings" variant="secondary" class="font-bold"
                    color="gray" size="md" v-if="!isLoading" @click="isSettingsModalVisible = true"
                    :aria-label="t('openSettings')">
                    <template #icon>
                        <Settings01Icon />
                    </template>
                </HLButton>
                <div v-else>
                    <HLSkeleton width="40px" height="40px" id="pg_agency_location_reporting-skeleton-settings_btn" :round="false" />
                </div>
            </div>
        </div>

        <HLDataTableWrapper id="pg_agency_location_reporting-container-schedules_table" max-width="100%" showHeader
            responsive-column-width v-if="!isLoading">
            <template #header-content-left>
                <div class="flex items-center gap-4 h-14 pl-4">
                    <HLDropdown id="pg_agency_location_reporting-dropdown-bulk_actions" trigger="click" :options="[
                        {
                            key: 'resume',
                            label: t('resumeSelected'),
                            type: 'icon',
                            icon: PlayIcon,
                            iconPlacement: 'left',
                        },
                        {
                            key: 'pause',
                            label: t('pauseSelected'),
                            type: 'icon',
                            icon: PauseCircleIcon,
                            iconPlacement: 'left',
                        },
                    ]" :show-arrow="false" v-if="selectedRows.length > 1 || isAllReportsSelected"
                        @select="handleBulkActionClick" :show-search="false">
                        <HLButton id="pg_agency_location_reporting-btn-bulk_actions" size="sm" variant="primary"
                            color="blue" :disabled="isBulkActionInProgress">
                            <template #iconRight>
                                <ChevronDownIcon class="h-5 w-5" />
                            </template>
                            {{ t('bulkActions') }}
                        </HLButton>
                    </HLDropdown>
                    <div class="flex items-center gap-1.5" v-if="selectedRows.length > 1 || isAllReportsSelected">
                        <div class="text-gray-900 text-xs">{{ t('xReportsSelected', { count: selectedScheduleCount }) }}
                        </div>
                        <div>•</div>
                        <HLButton variant="text" color="blue" size="2xs"
                            id="pg_agency_location_reporting-btn-select_all_reports" @click="selectAllReports">
                            {{ t('selectAllXReports', { count: table.totalCount }) }}
                        </HLButton>
                        <div>•</div>
                        <HLButton variant="text" color="blue" size="2xs"
                            id="pg_agency_location_reporting-btn-select_all_reports" @click="deselectAllReports">
                            {{ t('deselectAll') }}
                        </HLButton>
                    </div>
                    <HLTooltip id="pg_agency_location_reporting-tooltip-bulk_action_in_progress" variant="dark"
                        v-if="isBulkActionInProgress">
                        <template #trigger>
                            <HLIcon id="pg_agency_location_reporting-icon-bulk_action_in_progress" size="28" color="var(--warning-500)">
                                <AlertTriangleIcon />
                            </HLIcon>
                        </template>
                        {{ t('bulkActionInProgress') }}
                    </HLTooltip>

                </div>
            </template>
            <template #header-content-right>
                <div class="flex items-center gap-4 h-14 pr-4">
                    <HLBadge id="pg_agency_location_reporting-badge-status_filter" dot
                        :show="table.filter.status !== 'all'" size="xs" :offset="[-1, 2]" color="blue">
                        <HLDropdown id="pg_agency_location_reporting-dropdown-status_filter" trigger="click"
                            :options="statusFilterOptions" :show-arrow="false" @select="handleStatusFilterChange"
                            :show-search="false">
                            <HLButton id="pg_agency_location_reporting-btn-filter_dropdown_trigger" size="md">
                                <template #iconLeft>
                                    <FilterLinesIcon class="h-5 w-5" />
                                </template>
                                {{ t('scheduleStatus') }}
                            </HLButton>
                        </HLDropdown>
                    </HLBadge>
                    <div class="min-w-[340px]">
                        <HLInput id="pg_agency_location_reporting-input-search" size="md"
                            :placeholder="t('scheduleSearchPlaceholder')" v-model:model-value="table.search.text"
                            clearable>
                            <template #prefix>
                                <SearchSmIcon class="h-5 w-5" />
                            </template>
                        </HLInput>
                    </div>
                </div>
            </template>
            <HLDataTable id="pg_agency_location_reporting-table-schedules" ref="tableInstance" :columns="columns"
                :data="table.computedData" :horizontal-borders="true" :vertical-borders="false"
                :loading="table.isLoading" row-height="72px" headerRowHeight="44px" :row-hover="false"
                :column-hover="false" maxHeight="calc(100vh - 316px)">
                <template #no-data>
                    <HLEmpty id="pg_agency_location_reporting_schedule_table-empty" size="md" :title="t('noRecords')" />
                </template>
            </HLDataTable>
            <template #footer>
                <div class="flex justify-between items-center">
                    <div></div>
                    <HLPagination v-if="table" id="pg_agency_location_reporting_schedule_table-pagination"
                        :current-page="paginationConfig.page" :item-count="table.totalCount"
                        :per-page="paginationConfig.limit" :pages-to-display="7"
                        :per-page-text="t('subAccountsPerPage')" :per-page-dropdown-options="paginationConfig.options"
                        size="md" @update:page="pageChange" @update:per-page="rowCountChange">
                        <template #prev> {{ t('previous') }} </template>
                        <template #next> {{ t('next') }} </template>
                    </HLPagination>
                </div>
            </template>
        </HLDataTableWrapper>
        <div v-else class="h-[calc(100%-58px)] max-h-[882px]">
            <HLSkeleton width="100%" height="100%" id="pg_agency_location_reporting-skeleton-schedule_list_table" :round="false" />
        </div>
    </div>
    <HLModal id="pg_agency_location_reporting-modal-schedule_action" v-model:show="actionModalData.isVisible">
        <template #header>
            {{ t('scheduleAction') }}
        </template>
        {{ actionModalData.content }}
        <template #footer>
            <div id="pg_agency_location_reporting_schedule_action_modal-footer" class="flex justify-end gap-2 m-4">
                <HLButton id="pg_agency_location_reporting_schedule_action_modal_footer-btn-cancel" variant="secondary"
                    color="gray" @click="closeActionModal">
                    {{ t('cancel') }}
                </HLButton>
                <HLButton id="pg_agency_location_reporting_schedule_action_modal_footer-btn-confirm" variant="primary"
                    color="blue" @click="handleActionModalConfirm" :loading="actionModalData.isPrimaryBtnLoading">
                    {{ t('confirm') }}
                </HLButton>
            </div>
        </template>
    </HLModal>
    <HLModal id="pg_agency_location_reporting-modal-schedule_bulk_action" v-model:show="bulkActionModalData.isVisible">
        <template #header>
            {{ t('scheduleBulkAction') }}
        </template>
        {{ bulkActionModalData.content }}
        <template #footer>
            <div class="flex justify-end gap-2 m-4">
                <HLButton id="pg_agency_location_reporting_schedule_bulk_action_modal-btn-cancel" variant="secondary"
                    color="gray" @click="closeBulkActionModal">
                    {{ t('cancel') }}
                </HLButton>
                <HLButton id="pg_agency_location_reporting_schedule_bulk_action_modal-btn-confirm" variant="primary"
                    color="blue" @click="handleBulkActionModalConfirm"
                    :loading="bulkActionModalData.isPrimaryBtnLoading">
                    {{ t('confirm') }}
                </HLButton>
            </div>
        </template>
    </HLModal>
    <ScheduleSettingsModal v-model:is-visible="isSettingsModalVisible" :company-id="company.id"
        :is-auto-schedule-enabled="company.rollupAutoScheduleEnabled" v-if="isSettingsModalVisible"
        @enable-bulk-action-in-progress="onBulkPauseSchedule"
        @update:is-auto-schedule-enabled="handleAutoScheduleUpdate" :is-bulk-action-in-progress="isBulkActionInProgress" />
</template>